import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../api/client';
import type { MenuAdminCategory, MenuAdminProduct } from '../api/types';
import { Alert, Spinner, assetUrl, money } from '../components/common';
import { useAuth } from '../state/AuthContext';

/**
 * Editing the menu: names, descriptions, prices and pictures.
 *
 * Deliberately not the same screen as the sold-out toggles. That one is used
 * mid-shift with a queue waiting and applies every tap immediately; this one
 * changes what the shop sells and what it charges, so it has explicit Save
 * buttons and lives behind admin. Running out of bananas and repricing the menu
 * are not the same kind of decision and should not share a screen.
 */
export function StaffMenuEditPage() {
  const { customer: me } = useAuth();
  const canEdit = me?.role === 'Admin' || me?.role === 'SuperAdmin';

  const [menu, setMenu] = useState<MenuAdminCategory[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState<{ name: string; description: string }>({ name: '', description: '' });
  const [prices, setPrices] = useState<Record<string, string>>({});
  const fileInputs = useRef<Record<string, HTMLInputElement | null>>({});

  const load = useCallback(async () => {
    try {
      setMenu(await api.staffMenu());
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function run(id: string, action: () => Promise<unknown>, note?: string) {
    setBusy(id);
    setError(null);
    setMessage(null);

    try {
      await action();
      await load();
      if (note) setMessage(note);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  function startEdit(product: MenuAdminProduct) {
    setDraft({ name: product.name, description: product.description });
    setEditing(product.id);
    setError(null);
  }

  if (!menu) return <Spinner />;

  return (
    <section className="page">
      <h1>Edit the menu</h1>
      <p className="muted">
        Names, descriptions, prices and pictures. To mark something sold out for the day, use the{' '}
        <strong>Menu</strong> screen instead — that applies immediately and does not need saving.
      </p>

      {!canEdit && <Alert tone="info">Only an admin can change the menu. You can look, though.</Alert>}
      {error && <Alert>{error}</Alert>}
      {message && <Alert tone="success">{message}</Alert>}

      {menu.map((category) => (
        <div key={category.id}>
          <h2 className="section-heading">{category.name}</h2>

          {category.products.map((product) => (
            <div key={product.id} className="panel menuedit">
              <div className="menuedit__head">
                <div className="menuedit__pic">
                  {product.imageUrl ? (
                    <img src={assetUrl(product.imageUrl) ?? undefined} alt={product.name} />
                  ) : (
                    <span className="menuedit__nopic">No picture</span>
                  )}
                </div>

                <div className="menuedit__body">
                  {editing === product.id ? (
                    <>
                      <label className="field">
                        <span>Name</span>
                        <input
                          value={draft.name}
                          onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                        />
                      </label>
                      <label className="field">
                        <span>Description</span>
                        <textarea
                          rows={2}
                          value={draft.description}
                          onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                        />
                      </label>
                      <div className="rolebtns">
                        <button
                          type="button"
                          className="btn btn--primary btn--sm"
                          disabled={busy === product.id || !draft.name.trim()}
                          onClick={() =>
                            run(
                              product.id,
                              () =>
                                api.staffEditProduct(product.id, {
                                  name: draft.name.trim(),
                                  description: draft.description.trim(),
                                  sortOrder: product.sortOrder,
                                }),
                              'Saved.',
                            ).then(() => setEditing(null))
                          }
                        >
                          Save
                        </button>
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm"
                          onClick={() => setEditing(null)}
                        >
                          Cancel
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <h3 className="panel__title">{product.name}</h3>
                      <p className="muted small">{product.description || 'No description yet.'}</p>
                      {canEdit && (
                        <div className="rolebtns">
                          <button
                            type="button"
                            className="btn btn--ghost btn--sm"
                            onClick={() => startEdit(product)}
                          >
                            Edit name &amp; description
                          </button>

                          {/* The real input is hidden — a bare file input in a
                              list of cards reads as clutter. */}
                          <input
                            type="file"
                            accept="image/*"
                            hidden
                            ref={(el) => {
                              fileInputs.current[product.id] = el;
                            }}
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              e.target.value = '';
                              if (file) {
                                void run(
                                  product.id,
                                  () => api.staffSetProductImage(product.id, file),
                                  'Picture updated.',
                                );
                              }
                            }}
                          />
                          <button
                            type="button"
                            className="btn btn--ghost btn--sm"
                            disabled={busy === product.id}
                            onClick={() => fileInputs.current[product.id]?.click()}
                          >
                            {busy === product.id ? 'Uploading…' : product.imageUrl ? 'Replace picture' : 'Add a picture'}
                          </button>

                          {product.imageUrl && (
                            <button
                              type="button"
                              className="btn btn--ghost btn--sm"
                              disabled={busy === product.id}
                              onClick={() =>
                                run(product.id, () => api.staffClearProductImage(product.id), 'Picture removed.')
                              }
                            >
                              Remove picture
                            </button>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>

              <div className="menuedit__sizes">
                {product.variants.map((variant) => {
                  const key = variant.id;
                  const value = prices[key] ?? variant.price.toFixed(2);
                  const changed = Number(value) !== variant.price;

                  return (
                    <div key={key} className="menuedit__size">
                      <span className="menuedit__label">
                        {variant.label}
                        {variant.sizeMl ? ` · ${variant.sizeMl}ml` : ''}
                      </span>

                      {canEdit ? (
                        <>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={value}
                            onChange={(e) => setPrices({ ...prices, [key]: e.target.value })}
                          />
                          <button
                            type="button"
                            className="btn btn--primary btn--sm"
                            disabled={busy === key || !changed || Number.isNaN(Number(value))}
                            onClick={() =>
                              run(
                                key,
                                () =>
                                  api.staffEditVariant(key, {
                                    label: variant.label,
                                    sizeMl: variant.sizeMl,
                                    price: Number(value),
                                    sortOrder: variant.sortOrder,
                                  }),
                                `${product.name} ${variant.label} is now ${money(Number(value))}.`,
                              ).then(() => setPrices((p) => ({ ...p, [key]: '' })))
                            }
                          >
                            {busy === key ? 'Saving…' : changed ? 'Save price' : 'Saved'}
                          </button>
                        </>
                      ) : (
                        <span className="muted">{money(variant.price)}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      ))}
    </section>
  );
}
